import React, { useState } from 'react';
import { Alert, Button, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from '../../shared/i18n';
import { stripeErrorMessage } from '../utils/stripeErrors';
import { formatMoney } from '../../shared/utils/money';

/** One inline payment error: short localized text, raw gateway text only in the tooltip. */
export const PaymentErrorAlert = ({ error, style }) => {
    if (!error) return null;
    return (
        <Alert
            type="error"
            showIcon
            style={style}
            message={error.text}
            action={error.detail ? (
                <Tooltip title={error.detail}><InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} /></Tooltip>
            ) : null}
        />
    );
};

/**
 * Stripe Payment Element checkout (inside <Elements options={{clientSecret, customPaymentMethods}}>).
 * The methods shown come from the Stripe Dashboard (cards, Apple Pay, Google Pay, Link, ACH, bank transfer,
 * Cash App Pay, Amazon Pay, Klarna…). Custom methods (cpmt_…) in `customMethods` are ours to collect:
 * selecting one calls onCustomMethod(handler) instead of confirming with Stripe.
 *
 * onDone(status, paymentIntent): 'succeeded' | 'processing' | 'requires_action' (bank transfer instructions / microdeposits).
 * The invoice is only marked paid by our webhook; the caller just shows state.
 */
// amount/currency = what Stripe charges (the user's display currency when it differs from the bill);
// settleAmount/settleCurrency = the bill (USD), shown as "≈ USD x.xx".
const StripePaymentElementForm = ({ amount, currency, settleAmount, settleCurrency, returnUrl, customMethods = [], onDone, onCustomMethod }) => {
    const { t, i18n } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    // Apple Pay / Google Pay one-tap buttons (Express Checkout Element); only shown where the device supports them
    const [walletReady, setWalletReady] = useState(false);

    const custom = customMethods.find((m) => m.id === selectedType);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            if (custom) {
                const { error: submitError } = await elements.submit();
                if (submitError) return; // the Payment Element shows field errors inline
                await onCustomMethod?.(custom.handler);
                return;
            }
            const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: returnUrl },
                redirect: 'if_required',
            });
            if (stripeError) {
                // validation errors are already shown inside the Payment Element
                if (stripeError.type !== 'validation_error') {
                    console.warn('Stripe confirmPayment error', stripeError);
                    setError(stripeErrorMessage(stripeError, t));
                }
                return;
            }
            handleIntent(paymentIntent);
        } catch (err) {
            console.warn('Payment submit failed', err);
            setError(stripeErrorMessage(err, t));
        } finally {
            setSubmitting(false);
        }
    };

    const handleIntent = (paymentIntent) => {
        const st = paymentIntent?.status;
        if (st === 'succeeded' || st === 'processing' || st === 'requires_action') {
            onDone?.(st, paymentIntent);
        } else {
            setError(stripeErrorMessage(paymentIntent?.last_payment_error || null, t));
        }
    };

    // Apple Pay / Google Pay sheet approved → confirm the same PaymentIntent (settled by our webhook)
    const handleExpressConfirm = async () => {
        if (!stripe || !elements) return;
        setError(null);
        const { error: submitError } = await elements.submit();
        if (submitError) return;
        const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required',
        });
        if (stripeError) {
            console.warn('Express checkout confirm error', stripeError);
            setError(stripeErrorMessage(stripeError, t));
            return;
        }
        handleIntent(paymentIntent);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div style={{ display: walletReady ? 'block' : 'none', marginBottom: 16 }}>
                <ExpressCheckoutElement
                    options={{
                        buttonHeight: 44,
                        buttonType: { applePay: 'plain', googlePay: 'plain' },
                        // wallets only; Link / Amazon Pay / Klarna stay in the Payment Element below
                        paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'never', amazonPay: 'never', paypal: 'never', klarna: 'never' },
                    }}
                    onReady={({ availablePaymentMethods }) => setWalletReady(!!(availablePaymentMethods && (availablePaymentMethods.applePay || availablePaymentMethods.googlePay)))}
                    onLoadError={(e) => { console.warn('Express Checkout unavailable', e); setWalletReady(false); }}
                    onConfirm={handleExpressConfirm}
                />
            </div>
            <PaymentElement
                options={{
                    // Card first and open (it can renew automatically); every other method folds under Stripe's "More" row
                    layout: { type: 'accordion', defaultCollapsed: false, radios: true, spacedAccordionItems: false, visibleAccordionItemsCount: 1 },
                    // Apple Pay / Google Pay are offered by the one-tap buttons above (same device check), not twice
                    wallets: { applePay: 'never', googlePay: 'never' },
                    paymentMethodOrder: String(i18n.language || '').startsWith('zh') ? ['card', 'alipay', 'wechat_pay'] : ['card'],
                }}
                onChange={(ev) => {
                    setSelectedType(ev.value?.type || null);
                    setError(null);
                }}
            />
            <PaymentErrorAlert error={error} style={{ marginTop: 16 }} />
            <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                disabled={!stripe || !elements}
                style={{ marginTop: 16 }}
            >
                {custom ? t('Continue') : t('Pay {{amount}}', { amount: formatMoney(amount, currency, { original: true }) })}
            </Button>
            {settleCurrency && currency && String(settleCurrency).toUpperCase() !== String(currency).toUpperCase() && (
                <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                    {t('Charged in {{currency}} · ≈ {{amount}}', { currency: String(currency).toUpperCase(), amount: formatMoney(settleAmount, settleCurrency, { original: true }) })}
                </Typography.Text>
            )}
        </form>
    );
};

export default StripePaymentElementForm;
